import React, { useState, useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";

const AccessControl = () => {
    const [employeeData, setEmployeeData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageGroupStart, setPageGroupStart] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const ITEMS_PER_PAGE = 10;
    const PAGE_GROUP_SIZE = 5;
    const API_URL = process.env.REACT_APP_FRONTEND_URL;
    const avatarBgClass = (seed = "") => {
        const palette = [
            "bg-sky-500",
            "bg-indigo-500",
            "bg-emerald-500",
            "bg-rose-500",
            "bg-amber-500",
            "bg-teal-500",
            "bg-fuchsia-500",
            "bg-cyan-500",
        ];
        let hash = 0;
        for (let i = 0; i < seed.length; i++)
            hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
        return palette[hash % palette.length];
    };
    const [searchEmployeeNo, setSearchEmployeeNo] = useState("");
    const getInitials = (name = "") => {
        const parts = name.trim().split(" ");
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    /* -------------------- Fetch Data -------------------- */
    const fetchData = async (page = 1, employeeNo = "") => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit: ITEMS_PER_PAGE,
            });

            if (employeeNo) params.append("employee_no", employeeNo);

            const url = `${API_URL}/v1/hris/user/get-all-user-sessions?${params.toString()}`;
            const response = await fetch(url);
            const result = await response.json();

            if (result.success) {
                setEmployeeData(result.data || []);
                setTotalRecords(result.totalCount || 0);
                setTotalPages(Math.ceil((result.totalCount || 0) / ITEMS_PER_PAGE));
            } else {
                setEmployeeData([]);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchData(currentPage, searchEmployeeNo);
    }, [currentPage]);


    /* -------------------- Pagination -------------------- */
    const visiblePageNumbers = [];
    const endPage = Math.min(pageGroupStart + PAGE_GROUP_SIZE - 1, totalPages);
    for (let i = pageGroupStart; i <= endPage; i++) {
        visiblePageNumbers.push(i);
    }

    /* -------------------- Delete Actions -------------------- */
    const openDeleteModal = (item) => {
        setDeleteTarget(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget?.user_id) {
            console.error("❌ Missing user_id in delete target");
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch(
                `${API_URL}/v1/hris/user/remove-device/${deleteTarget.user_id}`,
                { method: "DELETE" }
            );
            if (!response.ok) throw new Error("Failed to revoke access");
            const result = await response.json();

            console.log("✅ Deleted:", result);
            setShowDeleteModal(false);
            await fetchData(currentPage);
        } catch (error) {
            console.error("❌ Delete failed:", error);
        } finally {
            setIsLoading(false);
            setDeleteTarget(null);
        }
    };


    const cancelDelete = () => {
        setShowDeleteModal(false);
        setDeleteTarget(null);
    };

    /* -------------------- Render -------------------- */
    return (
        <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4 }}
        >
            <div className=" min-h-screen">
                <p className="text-[25px] my-5">
                    Device Access Control
                </p>

                {/* Search Bar */}
                <div className="flex justify-end items-center mb-4">
                    <input
                        type="text"
                        placeholder="Search by Employee No..."
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        onChange={(e) => setSearchEmployeeNo(e.target.value)}
                        value={searchEmployeeNo}
                    />
                    <button
                        onClick={() => {
                            setCurrentPage(1);
                            fetchData(1, searchEmployeeNo); // 🔍 Trigger filter
                        }}
                        className="ml-2 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                    >
                        Search
                    </button>
                    <button
                        onClick={() => {
                            setSearchEmployeeNo("");
                            setCurrentPage(1);
                            fetchData(1, ""); // 🔄 Reset filter
                        }}
                        className="ml-2 px-4 py-2 bg-gray-100 border border-gray-300 text-sm rounded hover:bg-gray-200 transition"
                    >
                        Reset
                    </button>
                </div>

                <div className="bg-white rounded-lg shadow">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="text-gray-600 bg-gray-100 uppercase text-xs">
                                <tr>
                                    <th className="p-4">Employee</th>
                                    <th className="p-4">Employee No</th>
                                    <th className="p-4">Device Name</th>
                                    <th className="p-4">Device ID</th>
                                    <th className="p-4">Last Active</th>
                                    <th className="p-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : employeeData.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500">
                                            No records found
                                        </td>
                                    </tr>
                                ) : (
                                    employeeData.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${avatarBgClass(
                                                            item.employee_no
                                                        )}`}
                                                    >
                                                        {getInitials(item.employee_no)}
                                                    </div>
                                                    <span className="font-medium text-gray-800">
                                                        {item.employee_no}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-700">{item.employee_no}</td>
                                            <td className="p-4 text-gray-700">{item.device_name}</td>
                                            <td className="p-4 text-gray-600 font-mono text-xs">
                                                {item.device_id}
                                            </td>
                                            <td className="p-4 text-gray-600 text-xs">
                                                {new Date(item.updated_at).toLocaleString()}
                                            </td>
                                            <td className="p-4">
                                                <button
                                                    onClick={() => openDeleteModal(item)}
                                                    disabled={isLoading}
                                                    className="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-white hover:bg-red-600 border border-red-600 rounded-md transition-colors disabled:opacity-50"
                                                >
                                                    Revoke Access
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center p-4 border-t border-gray-200 text-sm text-gray-600">
                        <span>
                            Showing{" "}
                            {employeeData.length > 0
                                ? (currentPage - 1) * ITEMS_PER_PAGE + 1
                                : 0}{" "}
                            to {Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)} of{" "}
                            {totalRecords} entries
                        </span>

                        <div className="flex gap-1 items-center">
                            {pageGroupStart > 1 && (
                                <button
                                    onClick={() => {
                                        const newStart = Math.max(pageGroupStart - PAGE_GROUP_SIZE, 1);
                                        setPageGroupStart(newStart);
                                        setCurrentPage(newStart);
                                        fetchData(newStart, searchEmployeeNo); // ✅ keep filter
                                    }}

                                    className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100"
                                >
                                    ← Prev
                                </button>
                            )}

                            {visiblePageNumbers.map((n) => (
                                <button
                                    onClick={() => {
                                        const newStart = pageGroupStart + PAGE_GROUP_SIZE;
                                        setPageGroupStart(newStart);
                                        setCurrentPage(newStart);
                                        fetchData(newStart, searchEmployeeNo); // ✅ keep filter
                                    }}

                                    className={`px-3 py-1.5 border rounded-md transition-colors ${currentPage === n
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    {n}
                                </button>
                            ))}


                            {pageGroupStart + PAGE_GROUP_SIZE <= totalPages && (
                                <button
                                    onClick={() => {
                                        const newStart = pageGroupStart + PAGE_GROUP_SIZE;
                                        setPageGroupStart(newStart);
                                        setCurrentPage(newStart);
                                    }}
                                    className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100"
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---------------- Delete Modal ---------------- */}
                {showDeleteModal && deleteTarget && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 relative">
                            <button
                                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                                onClick={cancelDelete}
                            >
                                <X size={18} />
                            </button>

                            <div className="flex flex-col items-center text-center space-y-3">
                                <AlertTriangle className="text-red-500" size={40} />
                                <h2 className="text-lg font-semibold text-gray-800">
                                    Confirm Revocation
                                </h2>
                                <p className="text-gray-600 text-sm">
                                    Are you sure you want to revoke access for{" "}
                                    <span className="font-semibold">{deleteTarget.employee_no}</span>{" "}
                                    on device{" "}
                                    <span className="font-semibold">
                                        {deleteTarget.device_name}
                                    </span>
                                    ?
                                </p>

                                <div className="flex gap-3 mt-4">
                                    <button
                                        onClick={cancelDelete}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                    >
                                        Yes, Revoke
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AccessControl;
